import { Check } from "lucide-react-native";
import { FC, useMemo } from "react";
import { View } from "react-native";

import { BarChart, LineChart, useCopyableChart } from "@/components/charts";
import { Box, Card, HStack, Text, VStack } from "@/components/ui";
import { useLocalization, useThemeColors } from "@/hooks";
import SampleEditorialSummary from "./_SampleEditorialSummary";

/* ── Types ─────────────────────────────────────────────── */

export interface TURFReportData {
  /** Index into `items` of the recommended portfolio (the chosen
      portfolio size — typically the elbow of the reach curve). */
  elbowPortfolio: number[];
  /** Reach for each portfolio size 1..items.length, expressed as a
      percentage of the sample (0..100). `reachBySize[k-1]` is the
      cumulative unduplicated reach for the best portfolio of size k. */
  reachBySize: number[];
  items: { id: string; name: string }[];
  /** Raw selection counts per item (multi-select tally). */
  perItemCounts: number[];
  sampleSize: number;
}

interface TURFReportProps {
  data: TURFReportData;
  showSampleEditorialSummary?: boolean;
}

/* ── Component ──────────────────────────────────────────── */

const TURFReport: FC<TURFReportProps> = ({
  data,
  showSampleEditorialSummary = false,
}) => {
  const { i18n } = useLocalization();
  const themeColors = useThemeColors();
  const primary = themeColors.primary;

  const { chartRef: reachRef, copyButton: reachCopyButton } =
    useCopyableChart();
  const { chartRef: countsRef, copyButton: countsCopyButton } =
    useCopyableChart();

  const fmtPctRound = (n: number) =>
    i18n.numberToPercentage(n, { precision: 0 });

  const elbowSize = data.elbowPortfolio.length;
  const elbowReach = elbowSize > 0 ? (data.reachBySize[elbowSize - 1] ?? 0) : 0;

  /* No answers yet: render the shell, hand the charts empty data (they show
     their own "No data available"), and show "—" instead of a fabricated reach —
     mirroring how `PricingResult` shows "—" rather than a fabricated price. */
  const hasData = data.sampleSize > 0;

  const series = useMemo(
    () => [
      {
        color: primary,
        key: "reach",
        label: i18n.t("useCasePage.turf.sampleReport.chart.reachLabel"),
        points: data.reachBySize.map((reach, idx) => ({
          x: idx + 1,
          y: reach,
        })),
      },
    ],
    [data.reachBySize, i18n, primary]
  );

  const referencePoints = useMemo(
    () =>
      elbowSize > 0
        ? [
            {
              color: "hsl(222, 24%, 12%)",
              label: i18n.t("useCasePage.turf.sampleReport.chart.elbowLabel"),
              x: elbowSize,
              y: elbowReach,
            },
          ]
        : [],
    [elbowReach, elbowSize, i18n]
  );

  const rawPoints = useMemo(
    () =>
      data.items.map((it, idx) => ({
        x: it.name,
        y: data.perItemCounts[idx] ?? 0,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, i18n]
  );

  if (data.items.length === 0) {
    return null;
  }

  return (
    <VStack className="gap-3">
      {showSampleEditorialSummary && (
        <SampleEditorialSummary
          body={i18n.t("useCasePage.turf.sampleReport.summary.body")}
          headline={i18n.t("useCasePage.turf.sampleReport.summary.headline")}
        />
      )}

      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-5">
          <VStack className="gap-3 lg:flex-row lg:items-end lg:justify-between lg:gap-4">
            <VStack className="gap-1 lg:pb-2 lg:flex-1">
              <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
                {`${i18n.t("useCasePage.turf.sampleReport.portfolio.title")} (${i18n.t("dashboardNavigator.respondents", { count: data.sampleSize })})`}
              </Text>
              <Text className="text-xs text-typography-400">
                {i18n.t("useCasePage.turf.sampleReport.portfolio.subtitle", {
                  size: elbowSize,
                })}
              </Text>
            </VStack>
            <VStack className="items-center lg:items-end gap-0">
              <Text
                className="text-3xl font-bold tracking-tight"
                style={{ color: primary }}
              >
                {hasData ? fmtPctRound(elbowReach) : "—"}
              </Text>
              <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide">
                {i18n.t("useCasePage.turf.sampleReport.portfolio.reachLabel")}
              </Text>
            </VStack>
          </VStack>

          <VStack className="gap-2">
            {data.elbowPortfolio.map((idx, rank) => {
              const it = data.items[idx];
              if (!it) return null;
              return (
                <HStack
                  className="items-center gap-3 px-4 py-3 rounded-xl bg-background-50 border border-outline-100"
                  key={it.id}
                >
                  <Box
                    className="w-6 h-6 rounded-full items-center justify-center"
                    style={{ backgroundColor: primary }}
                  >
                    <Check color="white" size={14} strokeWidth={3} />
                  </Box>
                  <Text className="text-2xs font-semibold text-typography-400 w-5">
                    {String(rank + 1).padStart(2, "0")}
                  </Text>
                  <Text className="text-sm font-medium text-typography-800 flex-1">
                    {it.name}
                  </Text>
                </HStack>
              );
            })}
          </VStack>
        </VStack>
      </Card>

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {reachCopyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.turf.sampleReport.chart.title")} (${i18n.t("dashboardNavigator.respondents", { count: data.sampleSize })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.turf.sampleReport.chart.subtitle")}
            </Text>
          </VStack>

          <View ref={reachRef} style={{ width: "100%" }}>
            <Box className="items-center">
              <LineChart
                formatX={(v) => `${v}`}
                formatY={(v) => `${v.toFixed(0)}%`}
                height={320}
                referencePoints={hasData ? referencePoints : []}
                series={hasData ? series : []}
                xDomain={[1, data.items.length]}
                xLabel={i18n.t("useCasePage.turf.sampleReport.chart.xLabel")}
                yDomain={[0, 100]}
                yLabel={i18n.t("useCasePage.turf.sampleReport.chart.yLabel")}
              />
            </Box>
          </View>
        </VStack>
      </Card>

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {countsCopyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.turf.sampleReport.rawCounts.title")} (${i18n.t("dashboardNavigator.respondents", { count: data.sampleSize })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.turf.sampleReport.rawCounts.subtitle")}
            </Text>
          </VStack>
          <View ref={countsRef} style={{ width: "100%" }}>
            <BarChart
              points={hasData ? rawPoints : []}
              totalRespondents={data.sampleSize}
            />
          </View>
        </VStack>
      </Card>
    </VStack>
  );
};

export default TURFReport;
